import { useEffect, useState, useCallback } from "react";
import NotFound from "./NotFound";

const FUNCTIONS = [
  "notify-profile-published",
  "notify-connection-request",
  "send-scheduling-email",
  "notify-new-message",
  "notify-discussion-reply",
  "notify-help-request",
  "notify-call-cancelled",
  "notify-learning-assignment",
  "notify-times-submitted",
  "send-meeting-proposal-notification",
];

const STRICT_CORS_FUNCTIONS = new Set([
  "notify-profile-published",
  "notify-connection-request",
]);

type Row = {
  name: string;
  status: number | "ERR";
  result: "pass" | "FAIL" | "ERR";
  error?: string;
};

const DevSecurityTest = () => {
  if (import.meta.env.PROD) return <NotFound />;

  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    setRows([]);
    const base = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

    const results = await Promise.all(
      FUNCTIONS.map(async (name): Promise<Row> => {
        try {
          const res = await fetch(`${base}/${name}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          });
          return {
            name,
            status: res.status,
            result: res.status === 401 ? "pass" : "FAIL",
          };
        } catch (e: any) {
          if (STRICT_CORS_FUNCTIONS.has(name) && e?.message === "Failed to fetch") {
            return {
              name,
              status: "ERR",
              result: "pass",
              error: "Strict CORS rejected preview origin",
            };
          }

          return { name, status: "ERR", result: "ERR", error: e?.message ?? String(e) };
        }
      })
    );

    setRows(results);
    setRunning(false);
  }, []);

  useEffect(() => { run(); }, [run]);

  const passed = rows.filter((r) => r.result === "pass").length;

  return (
    <div className="p-8 font-mono text-sm max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Edge Function Auth Test (dev only)</h1>
      <p className="mb-4 text-muted-foreground">
        Calling each function with NO Authorization header. Expecting HTTP 401, or a strict CORS
        rejection for origin-locked functions in preview.
      </p>
      <button
        onClick={run}
        disabled={running}
        className="mb-6 px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50"
      >
        {running ? "Running..." : "Re-run tests"}
      </button>

      <div className="mb-4 font-semibold">
        {rows.length > 0 && `${passed} / ${FUNCTIONS.length} passed`}
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2">Function</th>
            <th className="text-left py-2">Status</th>
            <th className="text-left py-2">Result</th>
          </tr>
        </thead>
        <tbody>
          {FUNCTIONS.map((name) => {
            const row = rows.find((r) => r.name === name);
            return (
              <tr key={name} className="border-b">
                <td className="py-2">{name}</td>
                <td className="py-2">{row ? row.status : running ? "…" : "-"}</td>
                <td
                  className={`py-2 font-bold ${
                    row?.result === "pass"
                      ? "text-green-600"
                      : row?.result
                      ? "text-red-600"
                      : ""
                  }`}
                >
                  {row?.result ?? ""}
                  {row?.error ? ` (${row.error})` : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DevSecurityTest;
